/**
 * DechetsSection — bloc pliable « Collecte des déchets » du pôle info administré.
 *
 * Affiché seulement si la commune a saisi des collectes (le backend renvoie
 * `dechets: null` sinon → masquage auto). L'administré :
 *   · choisit son secteur (si la commune en a plusieurs) ;
 *   · voit la prochaine collecte de chaque flux (date en clair, consigne) ;
 *   · active/désactive le rappel push la veille.
 *
 * Réutilise le socle existant : SectionRepliable (pliable), api.dechets, et les
 * couleurs par flux (bac jaune = warning, verre = success, etc.). Aucune modale
 * navigateur (Règle projet) — les retours se font via un petit message inline.
 */

import { useState } from 'react';
import { Trash2, BellRing } from 'lucide-react';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { Badge, Button } from '@/components/ui';
import { api, ApiException, type DechetsCitoyenPayload, type DechetFlux } from '@/lib/api';

const FLUX_LABEL: Record<DechetFlux, string> = {
  om: 'Ordures ménagères',
  tri: 'Tri sélectif (bac jaune)',
  verre: 'Verre',
  vegetaux: 'Déchets végétaux',
  encombrants: 'Encombrants',
};

/** Pastille de couleur par flux (via classes de la palette existante). */
const FLUX_DOT: Record<DechetFlux, string> = {
  om: 'bg-ink-muted',
  tri: 'bg-warning',
  verre: 'bg-success',
  vegetaux: 'bg-urba',
  encombrants: 'bg-info',
};

/** Date pure yyyy-mm-dd → « jeudi 3 juillet » (UTC pour ne pas décaler). */
function formatDatePure(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });
}

/** Renvoie « demain », « aujourd'hui » ou la date, pour la collecte la plus proche. */
function libelleProximite(dateStr: string): string | null {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  const [ty, tm, td] = today.split('-').map(Number);
  const [dy, dm, dd] = dateStr.split('-').map(Number);
  const t = Date.UTC(ty!, tm! - 1, td!);
  const c = Date.UTC(dy!, dm! - 1, dd!);
  const diff = Math.round((c - t) / 86400000);
  if (diff <= 0) return "aujourd'hui";
  if (diff === 1) return 'demain';
  return null;
}

export function DechetsSection({
  data,
  onUpdated,
}: {
  data: DechetsCitoyenPayload;
  onUpdated: (next: DechetsCitoyenPayload | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const prochaine = data.prochaines[0] ?? null;
  const proximite = prochaine ? libelleProximite(prochaine.date) : null;

  async function changerSecteur(secteurId: string) {
    setBusy(true);
    setMsg(null);
    try {
      const next = await api.dechets.setPreference({ secteurId, rappelActif: data.rappelActif });
      onUpdated(next);
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Erreur lors du changement de secteur');
    } finally {
      setBusy(false);
    }
  }

  async function toggleRappel() {
    setBusy(true);
    setMsg(null);
    try {
      const next = await api.dechets.setPreference({
        secteurId: data.secteurChoisiId,
        rappelActif: !data.rappelActif,
      });
      onUpdated(next);
      setMsg(next?.rappelActif
        ? 'Rappel activé — vous serez prévenu·e la veille de la collecte.'
        : 'Rappel désactivé.');
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Erreur lors de la mise à jour du rappel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionRepliable
      title="Collecte des déchets"
      icon={<Trash2 className="h-5 w-5 text-grc" aria-hidden="true" />}
      defaultOpen
      right={proximite
        ? <Badge variant="warning" size="sm">Collecte {proximite}</Badge>
        : <Badge variant="neutral" size="sm">Calendrier</Badge>}
    >
      {/* Sélecteur de secteur (si plusieurs) */}
      {data.secteurs.length > 1 && (
        <div className="mb-3">
          <label htmlFor="dechets-secteur" className="mb-1 block text-xs font-medium text-ink-muted">
            Votre secteur
          </label>
          <select
            id="dechets-secteur"
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            value={data.secteurChoisiId ?? data.secteurs[0]!.id}
            disabled={busy}
            onChange={(e) => changerSecteur(e.target.value)}
          >
            {data.secteurs.map((s) => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </div>
      )}

      {/* Prochaines collectes */}
      {data.prochaines.length > 0 ? (
        <ul className="space-y-2">
          {data.prochaines.map((c) => {
            const prox = libelleProximite(c.date);
            return (
              <li key={`${c.flux}-${c.date}`} className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
                <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${FLUX_DOT[c.flux]}`} aria-hidden="true" />
                <div className="min-w-0">
                  <div className="font-medium text-ink">{FLUX_LABEL[c.flux]}</div>
                  <div className="mt-0.5 text-sm text-ink-muted">
                    {prox ? <span className="font-medium text-ink">{prox}</span> : formatDatePure(c.date)}
                    {prox && <span className="text-ink-dim"> · {formatDatePure(c.date)}</span>}
                  </div>
                  {c.consigne && <div className="mt-0.5 text-xs text-ink-dim italic">{c.consigne}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-ink-muted">Aucune collecte à venir pour ce secteur.</p>
      )}

      {/* Rappel push */}
      <div className="mt-3 flex items-center justify-between gap-3 rounded-r-lg border border-border bg-bg-2 p-3">
        <div className="flex items-center gap-2 text-sm text-ink">
          <BellRing className="h-4 w-4 text-grc" aria-hidden="true" />
          Me rappeler la veille de la collecte
        </div>
        <Button variant={data.rappelActif ? 'primary' : 'ghost'} size="sm" onClick={toggleRappel} disabled={busy}>
          {data.rappelActif ? 'Activé' : 'Activer'}
        </Button>
      </div>
      {msg && <p className="mt-2 text-xs text-ink-muted" role="status">{msg}</p>}
    </SectionRepliable>
  );
}
